import { format, isSameDay } from "date-fns";
import { MapPin, Users, Clock } from "lucide-react";
import { activityDayRange } from "@/lib/calendar-layout";

export default function CalendarList({ activities, onActivityClick }) {
    if (activities.length === 0) {
        return (
            <div className="border border-[#E5E5E3] bg-white p-16 text-center" data-testid="calendar-list-empty">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-3">Nothing scheduled</p>
                <p className="font-heading text-2xl font-light text-[#111110]">A quiet stretch on the calendar.</p>
                <p className="text-sm text-[#666664] mt-2">Create an activity to get things started.</p>
            </div>
        );
    }

    // Expand multi-day activities into one entry per covered local day, then sort/group.
    const expanded = [];
    for (const a of activities) {
        const days = activityDayRange(a);
        days.forEach((day, idx) => expanded.push({
            activity: a,
            day,
            dayIndex: idx + 1,
            totalDays: days.length,
        }));
    }
    expanded.sort((x, y) => {
        if (x.day.getTime() !== y.day.getTime()) return x.day.getTime() - y.day.getTime();
        return new Date(x.activity.start_time) - new Date(y.activity.start_time);
    });

    const groups = [];
    let current = null;
    for (const row of expanded) {
        if (!current || !isSameDay(current.date, row.day)) {
            current = { date: row.day, items: [] };
            groups.push(current);
        }
        current.items.push(row);
    }

    return (
        <div className="border border-[#E5E5E3] bg-white divide-y divide-[#E5E5E3]" data-testid="calendar-list">
            {groups.map((g, i) => {
                const dayKey = format(g.date, "yyyy-MM-dd");
                return (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-[180px_1fr]" data-testid={`list-day-${dayKey}`} data-event-ids={g.items.map(it => it.activity.id).join(",")}>
                        <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-[#E5E5E3] bg-[#FAFAF9]">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">{format(g.date, "EEEE")}</div>
                            <div className="font-heading text-3xl font-light text-[#111110] mt-1">{format(g.date, "MMM d")}</div>
                            <div className="text-xs text-[#666664] mt-1">{format(g.date, "yyyy")}</div>
                        </div>
                        <div className="divide-y divide-[#E5E5E3]">
                            {g.items.map(({ activity: a, dayIndex, totalDays }) => (
                                <button
                                    key={`${a.id}-${dayKey}`}
                                    onClick={() => onActivityClick?.(a)}
                                    className={`w-full text-left p-6 md:p-8 hover:bg-[#FAFAF9] transition-colors group relative ${a.is_past ? "opacity-60" : ""}`}
                                    data-testid={`list-event-${a.id}`}
                                    data-activity-id={a.id}
                                >
                                    <span className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: a.color || "#F0F0EE" }} />
                                    <div className="flex items-center gap-2 mb-2">
                                        {a.tag && <div className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">{a.tag}{a.is_past ? " · ended" : ""}</div>}
                                        {!a.tag && a.is_past && <div className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">Ended</div>}
                                        {totalDays > 1 && (
                                            <div className="text-[10px] tracking-[0.2em] uppercase text-[#888885] border border-[#E5E5E3] px-1.5 py-0.5" data-testid={`day-marker-${a.id}-${dayKey}`}>
                                                Day {dayIndex} of {totalDays}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-start justify-between gap-4">
                                        <h3 className="font-heading text-xl font-medium text-[#111110] group-hover:underline underline-offset-4">{a.title}</h3>
                                        <span className="shrink-0 text-sm text-[#333333] tabular-nums">
                                            {format(new Date(a.start_time), "HH:mm")} – {format(new Date(a.end_time), "HH:mm")}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-[#666664]">
                                        {a.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />{a.location}</span>}
                                        <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" strokeWidth={1.5} />{a.signup_count}{a.max_signups ? `/${a.max_signups}` : ""} signed up</span>
                                        <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} />{a.creator?.name}</span>
                                    </div>
                                    {a.description && <p className="mt-3 text-sm text-[#666664] line-clamp-2 max-w-2xl">{a.description}</p>}
                                </button>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
